import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import * as pathsActions from '../paths/pathsSlice';
import * as selectedItemsActions from '../selectedItems/selectedItemsSlice';
import process from 'process';
import { edgeApi } from '../services/edgeApi';
import { selectAllNodes, selectAllPoints } from '../map/slice/mapSelectors';
import { selectStartNode } from './plotterSelectors';

/**
 * @typedef {{
 *  center: [number, number],
 *  zoom: number,
 * }} MapView
 */

/**
 * @typedef {'SHOW' | 'HIDE' | 'AUTO'} LabelDisplayOption
 */

/**
 * @typedef {{
 *  appMode?: string,
 *  showCountyPoints: boolean,
 *  showFlowArrows: boolean,
 *  showEdgeDirections: boolean,
 *  showNoticeMarkers: boolean,
 *  showConstraints: boolean,
 *  showSegmentCapacityComponents: boolean,
 *  showPointCapacityComponents: boolean,
 *  showNoticeComponents: boolean,
 *  showPriceComponents: boolean,
 *  pathMode: "ANT" | "SIMPLE",
 *  nodes: {[key: string]: import('../services/edgeApi').Node},
 *  newNodes: {[key: string]: import('../services/edgeApi').Node},
 *  points: {[key: string]: import('../scenarios/scenarioSlice').Point},
 *  edges: {[key: string]: import('../services/edgeApi').Edge},
 *  riseChains: import('@/types').RiseChain[],
 *  riseChainsLoading: boolean,
 *  capacities: import('@/types').Flow[],
 *  markets: Array<{ description: string, price: number, point: number, id: number }>,
 *  localNode: import('../services/edgeApi').Node,
 *  localPoints: {[key: string]: import('../scenarios/scenarioSlice').Point},
 *  filtered: boolean,
 *  isolated: boolean,
 *  activeNodeId?: string,
 *  editPointId?: string,
 *  activeEdge?: import('../services/edgeApi').Edge,
 *  editNode: boolean,
 *  edgeMode: boolean,
 *  nodeSize: number,
 *  matchingPointId?: string,
 *  modal: boolean,
 *  showContracts: boolean,
 *  showRiseChains: boolean,
 *  mapView: MapView,
 *  layers: {
 *    counties: boolean,
 *    temperature: boolean,
 *    precipitation: boolean,
 *    wind: boolean,
 *  }
 *  labels: {
 *    compressors: LabelDisplayOption,
 *    points: LabelDisplayOption,
 *    interconnects: LabelDisplayOption,
 *  },
 *  mapFit: import('ol/coordinate').Coordinate[],
 *  focusedNode: {
 *    nodeId: string,
 *    zoom: number,
 *  } | null,
 * }} PlotterState
 */

// TODO: make localstorage for any slice or variable easy to turn on and off
let localNodeSize;
const DEFAULT_NODE_SIZE = 4;
export const MAX_TSPS_LABELS_THRESHOLD = 200;

try {
  localNodeSize = JSON.parse(
    localStorage.getItem('nodeSize') ?? DEFAULT_NODE_SIZE.toString(),
  );
} catch (e) {
  console.warn(e);
}

export const PATH_MODES = {
  ANT: 'ANT',
  SIMPLE: 'SIMPLE',
};

/**
 * @type {PlotterState}
 */
const initialState = {
  appMode: null,
  showCountyPoints: false,
  // This is getting kinda crowded - if we have more stuff to show/hide we should consider a "visibility" object or something
  showFlowArrows: true,
  showEdgeDirections: false,
  showNoticeMarkers: true,
  showConstraints: true,
  showSegmentCapacityComponents: false,
  showPointCapacityComponents: false,
  showPriceComponents: true,
  showNoticeComponents: false,
  pathMode: PATH_MODES.ANT,
  // =====================
  nodes: {},
  newNodes: {},
  points: {},
  edges: {},
  riseChains: [],
  riseChainsLoading: false,
  capacities: [],
  markets: [],
  // adding localNode in here to test it out. Not sure of best way to structure state yet
  localNode: {},
  localPoints: {},
  // TODO: add all filter conditions to filtered, instead of creating new things in state each time
  filtered: false,
  isolated: false,
  activeNodeId: null,
  editPointId: null,
  activeEdge: null,
  editNode: false,
  edgeMode: false,
  nodeSize: localNodeSize || 0,
  matchingPointId: null,
  modal: false,
  // showing all contracts by default right now - this should be set on a contract basis and handled in contractSlice
  showContracts: true,
  showRiseChains: false,
  mapView: null,
  layers: {
    counties: false,
    temperature: false,
    precipitation: false,
    wind: false,
  },
  labels: {
    compressors: 'AUTO',
    points: 'AUTO',
    interconnects: 'AUTO',
  },
  mapFit: null,
  focusedNode: null,
};

const addMapData = (state, action) => ({
  ...state,
  nodes: action.payload.nodes,
  points: action.payload.points,
  edges: action.payload.edges,
});

/**
 *
 * @param {PlotterState} state
 * @param {{
 *  payload: ChainedRiseTransport[]
 * }} action
 * @returns
 */
const setRiseChains = (state, action) => ({
  ...state,
  riseChains: action.payload,
});

/**
 *
 * @param {PlotterState} state
 * @param {{
 *  payload: boolean
 * }} action
 * @returns
 */
const setRiseChainsLoading = (state, action) => ({
  ...state,
  riseChainsLoading: action.payload,
});

const toggleFiltered = (state) => ({ ...state, filtered: !state.filtered });

const toggleShowCountyPoints = (state) => ({
  ...state,
  showCountyPoints: !state.showCountyPoints,
});

const setShowFlowArrows = (state, action) => ({
  ...state,
  showFlowArrows: Boolean(action.payload),
});

const setShowEdgeDirections = (state, action) => ({
  ...state,
  showEdgeDirections: Boolean(action.payload),
});

const setShowNoticeMarkers = (state, action) => ({
  ...state,
  showNoticeMarkers: Boolean(action.payload),
});

const setShowConstraints = (state, action) => ({
  ...state,
  showConstraints: Boolean(action.payload),
});

const setShowSegmentCapacityComponents = (state, action) => ({
  ...state,
  showSegmentCapacityComponents: Boolean(action.payload),
});

const setShowPointCapacityComponents = (state, action) => ({
  ...state,
  showPointCapacityComponents: Boolean(action.payload),
});

const setShowPriceComponents = (state, action) => ({
  ...state,
  showPriceComponents: Boolean(action.payload),
});

const setShowNoticeComponents = (state, action) => ({
  ...state,
  showNoticeComponents: Boolean(action.payload),
});

/**
 * @param {PlotterState} state
 * @param {{
 *  payload: {
 *    layer: keyof PlotterState["layers"],
 *    value: boolean
 *  }
 * }} action
 */
const setLayers = (state, action) => {
  const { layer, value } = action.payload;
  return {
    ...state,
    layers: {
      ...state.layers,
      [layer]: Boolean(value),
    },
  };
};

/**
 *
 * @param {PlotterState} state
 * @param {{
 *  payload: boolean
 * }} action
 */
const setShowRiseChains = (state, action) => ({
  ...state,
  showRiseChains: Boolean(action.payload),
});

/**
 *
 * @param {PlotterState} state
 * @param {{ payload: Capacity[] }} action
 */
const setCapacities = (state, action) => ({
  ...state,
  capacities: action.payload,
});

/**
 *
 * @param {PlotterState} state
 * @param {{
 *  payload: Array<{ description: string, price: nubmer, point: number, id: number }>
 * }} action
 * @returns
 */
const setMarkets = (state, action) => ({
  ...state,
  markets: action.payload,
});

const setPathMode = (state, action) => ({
  ...state,
  pathMode: action.payload,
});

/**
 *
 * @param {PlotterState} state
 * @param {{
 *  payload: MapView
 * }} action
 * @returns
 */
const setMapView = (state, action) => ({
  ...state,
  mapView: action.payload,
});

/**
 *
 * @param {PlotterState} state
 * @param {{
 *  payload: {nodeId: number, zoom?: number}
 * }} action
 */
const focusNode = (state, action) => {
  const { nodeId, zoom = 10 } = action.payload;
  return {
    ...state,
    focusedNode: {
      nodeId,
      zoom,
    },
  };
};

/**
 *
 * @param {PlotterState} state
 * @param {{
 *  payload: import('ol/coordinate').Coordinate[]
 * }} action
 * @returns
 */
const fitMapView = (state, action) => ({
  ...state,
  mapFit: action.payload,
});

const toggleIsolated = (state) => ({ ...state, isolated: !state.isolated });

const setActiveNodeId = (state, action) => {
  // delete state.nodes["new"]
  return {
    ...state,
    activeNodeId: action.payload,
    editNode: false,
    activeEdge: null,
  };
};

const setEditPointId = (state, action) => ({
  ...state,
  editPointId: action.payload,
});

const assignMatchingPoint = (state, _action) => {
  // add point to active node point array (remove from any other nodes?)
  // remove "new" point from node?
  // set point to edit
  // change point.node to activenode id (could be "new")
  const { activeNodeId } = state;
  const activeNode = state.nodes[activeNodeId];
  const { matchingPointId } = state;
  let updatedPoints = activeNode.points.filter(
    (point) => point != state.editPointId,
  );
  updatedPoints = updatedPoints.concat(matchingPointId);
  // remove previously edited point id
  // add matching point id

  return {
    ...state,
    editPointId: matchingPointId,
    nodes: {
      ...state.nodes,
      [activeNodeId]: {
        ...activeNode,
        points: updatedPoints,
      },
    },
  };
};

const assignPointToNode = (state, action) => {
  // add point to active node point array (remove from any other nodes?)
  // remove "new" point from node?
  // set point to edit
  // change point.node to activenode id (could be "new")
  const { node } = action.payload;
  const { point } = action.payload;
  // remove point from node just in case it's already in this node?
  // remove new point from node - do we need to potentially remove other point ids?
  let updatedPoints = node.points.filter(
    (nodePoint) => nodePoint != point.id && nodePoint != 'new',
  );
  // add matching point id
  updatedPoints = updatedPoints.concat(point.id);

  return {
    ...state,
    // editPointId: matchingPointId,
    localNode: {
      ...state.localNode,
      [node.id]: {
        ...node,
        points: updatedPoints,
      },
    },
    localPoints: {
      ...state.localPoints,
      [point.id]: {
        ...point,
        node: node.id,
      },
    },
  };
};

const addNewNode = (state, action) => {
  // console.log(action.payload)
  const lat = String(action.payload.lat);
  const lng = String(action.payload.lng);
  const newNode = {
    id: 'new',
    lat,
    lng,
    points: [],
    ends_edges: [],
    starts_edges: [],
  };
  return { ...state, localNode: newNode };
};

const setEditNode = (state, action) =>
  // need to set edit flag to true
  ({ ...state, editNode: action.payload });

const addEdges = (state, action) => ({
  ...state,
  edges: action.payload,
  activeEdge: { start: state.activeEdge?.end },
});

const setActiveEdge = (state, action) => {
  return { ...state, activeEdge: action.payload, activeNodeId: null };
};

/**
 *
 * @param {PlotterState} state
 * @param {void} _action
 * @returns
 */
const toggleContractsVisibility = (state, _action) => ({
  ...state,
  showContracts: !state.showContracts,
});

const setContractVisibility = (state, action) => ({
  ...state,
  showContracts: Boolean(action.payload),
});

const setStartNode = (state, action) => {
  // whenever setting first node, second node should be null, whether switching to new edge, or starting an edge to begin with
  // return {...state, firstNode: action.payload, secondNode: null}
  return {
    ...state,
    activeEdge: {
      ...state.activeEdge,
      start_node: action.payload,
    },
  };
};

/**
 *
 * @param {PlotterState} state
 * @param {{
 *  payload: {
 *    visible: boolean,
 *    chainId: number
 *  }
 * }} action
 */
const setRiseChainVisibility = (state, action) => {
  return {
    ...state,
    riseChains: state.riseChains.map((chain) =>
      chain.chain_id === action.payload.chainId
        ? { ...chain, visible: action.payload.visible }
        : chain,
    ),
  };
};

/**
 *
 * @param {PlotterState} state
 * @param {{
 *  payload: {
 *    visible: boolean,
 *  }
 * }} action
 */
const setRiseChainsVisibility = (state, action) => {
  return {
    ...state,
    riseChains: state.riseChains.map((path) => ({
      ...path,
      visible: action.payload.visible,
    })),
  };
};

const setEndNode = (state, action) => {
  // return {...state, secondNode: action.payload}
  return {
    ...state,
    activeEdge: {
      ...state.activeEdge,
      end_node: action.payload,
    },
  };
};

const setMatchingPointId = (state, action) => {
  return { ...state, matchingPointId: action.payload };
};

const updateActiveNode = (state, action) => {
  return {
    ...state,
    nodes: {
      ...state.nodes,
      [state.activeNodeId]: {
        ...state.nodes[state.activeNodeId],
        // this can probably be more general
        lat: action.payload.lat,
        lng: action.payload.lng,
      },
    },
  };
};

const setNodeSize = (state, action) => {
  try {
    localStorage.setItem('nodeSize', JSON.stringify(action.payload));
  } catch (e) {
    console.warn('Could not store node size in localStorage', e);
  }
  return { ...state, nodeSize: action.payload };
};

/**
 *
 * @param {PlotterState} state
 * @param {import('@reduxjs/toolkit').PayloadAction<{labelType: keyof PlotterState['labels'], value: LabelDisplayOption}>} action
 * @returns
 */
const setLabelDisplayOption = (state, action) => {
  const { labelType, value } = action.payload;
  return {
    ...state,
    labels: {
      ...state.labels,
      [labelType]: value,
    },
  };
};

// starting to name actions after events instead of setters - we'll see if it makes things more reusable
// this still just seems like a setter so maybe i'm not grasping the best way to do it.
const appModeChanged = (state, action) => ({
  ...state,
  appMode: action.payload.appMode,
});
const resetStore = (_state) => {
  // From here we can take action only at this "plotter" state
  // But, as we have taken care of this particular "reset" action
  // in rootReducer, we can use it to CLEAR the complete Redux Store's state
};

const reducers = {
  addMapData,
  setRiseChains,
  setRiseChainsLoading,
  setCapacities,
  setMarkets,
  toggleFiltered,
  toggleShowCountyPoints,
  setShowFlowArrows,
  setShowEdgeDirections,
  setShowNoticeMarkers,
  setShowConstraints,
  setShowRiseChains,
  setShowSegmentCapacityComponents,
  setShowPointCapacityComponents,
  setShowPriceComponents,
  setShowNoticeComponents,
  setLayers,
  setEditPointId,
  setPathMode,
  setMapView,
  fitMapView,
  focusNode,
  toggleIsolated,
  setActiveNodeId,
  assignMatchingPoint,
  assignPointToNode,
  addNewNode,
  setEditNode,
  addEdges,
  setActiveEdge,
  setRiseChainVisibility,
  setRiseChainsVisibility,
  toggleContractsVisibility,
  setContractVisibility,
  setStartNode,
  setEndNode,
  setMatchingPointId,
  updateActiveNode,
  setNodeSize,
  setLabelDisplayOption,
  appModeChanged,
  resetStore,
};

export const plotterSlice = createSlice({
  name: 'plotter',
  initialState,
  reducers,
});

export const fetchMapData = () => async (_dispatch) => {
  // let res = await axios.get(`${process.env.REACT_APP_API_URL}/api/map/`)
  //   // catch the error but this is really being handled in the interceptor - decide on best way to error handle
  //   .catch((error)=>{console.log("ERROR DETAIL: " + error.response.data.detail)});
  // if(res){
  //   dispatch(addMapData(res.data))
  //   // should this be a bunch of dispatches or just make one action that sets things back to "default"
  //   dispatch(setEditPointId(null))
  //   dispatch(setEditNode(false))
  // }
  // console.log("fetchMapData turned off")
  edgeApi.util.invalidateTags([
    { type: 'Point', id: 'LIST' },
    { type: 'Node', id: 'LIST' },
    { type: 'Edge', id: 'LIST' },
  ]);
};

export const createEdge = (edge) => (dispatch, _getState) => {
  // is it better to send the edge as a parameter or just get it from active edge state here?

  axios
    .post(`${process.env.REACT_APP_API_URL}/api/edges/`, edge)
    // .then((res) => dispatch(fetchMapData()))
    .then(() => dispatch(fetchEdges()));
};

export const createUpdateNode = () => (dispatch, getState) => {
  const { plotter } = getState();
  const activeNode = plotter.nodes[plotter.activeNodeId];
  if (activeNode.id == 'new' || !activeNode.id) {
    // if there's not an id, create it, otherwise update it
    axios
      .post(`${process.env.REACT_APP_API_URL}/api/nodes/`, activeNode)
      .then((res) => dispatch(setActiveNodeId(res.data)))
      .then(() => dispatch(fetchMapData()));
  } else {
    axios
      .put(
        `${process.env.REACT_APP_API_URL}/api/nodes/${activeNode.id}/`,
        activeNode,
      )
      .then(() => dispatch(fetchMapData()));
  }
};

export const deleteNode = (nodeId) => (dispatch) => {
  axios
    .delete(`${process.env.REACT_APP_API_URL}/api/nodes/${nodeId}/`)
    // this also needs to fetch edges because a node delete could delete associated edges
    .then(() => dispatch(fetchMapData()))
    .then(() => dispatch(setActiveNodeId(null)));
};

export const fetchEdges = () => (dispatch) => {
  axios
    .get(`${process.env.REACT_APP_API_URL}/api/edges/`)
    // .then((res) => console.log(res))
    .then((res) => dispatch(addEdges(res.data)));
};

export const fetchEdge = (edgeId) => (dispatch) => {
  axios
    .get(`${process.env.REACT_APP_API_URL}/api/edges/${edgeId}/`)

    .then((res) => dispatch(setActiveEdge(res.data)));
};

export const deleteEdge = (edgeId) => (dispatch) => {
  axios
    .delete(`${process.env.REACT_APP_API_URL}/api/edges/${edgeId}/`)
    .then(() => dispatch(fetchEdges()));
};

export const checkPointMatch = (pointAttribute) => (dispatch, getState) => {
  // ideally this would be set up to reuse the existing search modal functionality but I don't want to waste much time setting it up when this works
  // this should probably eventually be an API call to search for matching nodes but for now they will just all be in memory so we can do it here
  let matchingPoint;
  if (!pointAttribute) {
    matchingPoint = null;
  } else {
    // let points = getState().plotter.points
    // should this be all points or api points
    const points = selectAllPoints(getState());
    // expanding this to match loc_name as well - right now it will only match the most recently changed attribute, not a combination
    // find will only return one perfect match, this probably needs to be a filter
    matchingPoint = Object.keys(points).find(
      (key) =>
        points[key].loc_id === pointAttribute ||
        points[key.loc_name] === pointAttribute,
    );
    // return Object.keys(points).filter((key, index) => points[key].node == null).length
    if (matchingPoint) {
      dispatch(setMatchingPointId(matchingPoint));
    } else {
      dispatch(setMatchingPointId(null));
    }
  }
};

// eslint-disable-next-line react/display-name
export const markerClicked = (nodeId) => (dispatch, getState) => {
  // moved this here instead of in the event handler of the marker itself because the conditional event handler was probably causing excessive re-rendering
  const { appMode } = getState().plotter;
  const startNode = selectStartNode(getState());

  dispatch(setActiveNodeId(nodeId));

  // i'd rather have all this conditional logic for responding to clickHandlers depending on the appModes in pathsSlice but not sure how to do it cleanly
  // or have it in component state of applicable component but since we are having inputs from map and from sidebar, global state seems like the way to go

  switch (appMode) {
    case 'edgeBuilder':
      if (!startNode) {
        dispatch(setStartNode(nodeId));
      } else {
        dispatch(setEndNode(nodeId));
      }
      break;
    case 'allPaths':
      // should this send it over to pathsSlice, or keep it in this slice, or make a new slice for all mode stuff
      // dispatch(pathsActions.simplePathsClicked(nodeId));
      dispatch(pathsActions.allPathsClick(nodeId));
      break;
    default:
      // maybe set active node goes here instead of before this?
      // dispatch(setActiveNodeId(nodeId))
      return null;
  }
};

export const plotterActions = plotterSlice.actions;

export default plotterSlice.reducer;
